import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import * as Dialog from '@radix-ui/react-dialog'
import { X, Signal, RefreshCw, Loader2, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { camerasApi } from '@/api/endpoints'
import type { SIMUsage } from '@/types'

interface SIMDetailModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  cameraId: number
  cameraName: string
  iccid: string
  cachedUsage?: SIMUsage | null
}

function formatMB(mb: number): string {
  if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`
  return `${mb.toFixed(2)} MB`
}

function StatusBadge({ status }: { status: string }) {
  const isActive = status?.toLowerCase().includes('activat')
  const label = status?.replace(/_NAME$/, '').replace(/_/g, ' ') || 'Unknown'
  return (
    <span className={cn(
      'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium',
      isActive
        ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
        : 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
    )}>
      <span className={cn('h-1.5 w-1.5 rounded-full', isActive ? 'bg-emerald-500' : 'bg-amber-500')} />
      {label}
    </span>
  )
}

function InfoRow({ label, value, mono }: { label: string; value: string | number | null | undefined; mono?: boolean }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-border/40 last:border-0">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={cn('text-xs font-medium text-right max-w-[60%] truncate', mono && 'font-mono')}>{value || '—'}</span>
    </div>
  )
}

/** Smooth height transition — measures real content height and animates */
function ContentTransition({ children, triggerKey, open }: { children: React.ReactNode; triggerKey: string; open: boolean }) {
  const contentRef = useRef<HTMLDivElement>(null)
  const [height, setHeight] = useState<number | 'auto'>('auto')

  useEffect(() => {
    if (!open || !contentRef.current) return
    const frame = requestAnimationFrame(() => {
      if (contentRef.current) setHeight(contentRef.current.scrollHeight)
    })
    return () => cancelAnimationFrame(frame)
  }, [triggerKey, open])

  // Don't constrain height during close — let Radix exit animation run freely
  const isClosing = !open

  return (
    <div
      className="overflow-hidden"
      style={{
        height: isClosing ? 'auto' : (height === 'auto' ? 'auto' : height),
        transition: isClosing ? 'none' : 'height 300ms cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      <div ref={contentRef}>{children}</div>
    </div>
  )
}

export function SIMDetailModal({ open, onOpenChange, cameraId, cameraName, iccid, cachedUsage }: SIMDetailModalProps) {
  const { t } = useTranslation('cameras')

  // Fetch live data when modal opens
  const { data: liveData, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['sim-card-status', cameraId],
    queryFn: () => camerasApi.getSimCardStatus(cameraId),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  })

  // Use live data if available, fall back to cached bulk data
  const cardInfo = liveData?.card_info as Record<string, unknown> | undefined

  const monthUsed = Number(cardInfo?.monthUseFlow ?? cachedUsage?.monthUsedMB ?? 0)
  const monthRemaining = Number(cardInfo?.monthRemainFlow ?? cachedUsage?.monthRemainingMB ?? 0)
  const totalUsed = Number(cardInfo?.totalUseFlow ?? cachedUsage?.totalUsedMB ?? 0)
  const dataFlow = Number(cardInfo?.dataFlow ?? 0)
  const status = String(cardInfo?.cardStatus ?? cachedUsage?.status ?? '')
  const packageCode = String(cardInfo?.packageCode ?? cachedUsage?.packageName ?? '')
  const packageEndDate = String(cardInfo?.packageEndDate ?? cachedUsage?.packageEndDate ?? '')
  const activateDate = String(cardInfo?.activateDate ?? '')
  const expectedEndDate = String(cardInfo?.expectedEndDate ?? '')
  const imei = String(cardInfo?.imei ?? cachedUsage?.imei ?? '')
  const imsi = String(cardInfo?.imsi ?? '')
  const network = String(cardInfo?.network ?? cachedUsage?.networkStatus ?? '')
  const productCode = String(cardInfo?.productCode ?? '')
  const remainFlow = Number(cardInfo?.remainFlow ?? monthRemaining)
  const isRealName = cardInfo?.isRealName
  const isIMEIPool = cardInfo?.isIMEIPool
  const imeiPoolStatus = cardInfo?.imeiPoolStatus

  const totalPackage = monthUsed + monthRemaining
  const usagePercent = totalPackage > 0 ? Math.min((monthUsed / totalPackage) * 100, 100) : 0

  const hasData = cardInfo || cachedUsage

  // Decode package name to something readable
  const packageDisplay = packageCode
    ? packageCode.split('-').slice(0, 5).join(' ').replace(/BC |MBB |QT |EN /g, '').trim() || packageCode
    : '—'

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          style={{ animationDuration: '200ms' }}
        />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-xl border border-border bg-background shadow-2xl overflow-hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=open]:zoom-in-[0.98] data-[state=closed]:zoom-out-[0.98] data-[state=open]:slide-in-from-bottom-1 data-[state=closed]:slide-out-to-bottom-1"
          style={{ animationDuration: '250ms', animationTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)' }}
        >
          <Dialog.Title className="sr-only">{t('sim.title')}</Dialog.Title>

          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-border/60">
            <div className="flex items-center gap-2.5">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-purple-500/10">
                <Signal className="h-4 w-4 text-purple-500" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate">{t('sim.title')}</p>
                <p className="text-xs text-muted-foreground truncate">{cameraName}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {hasData && <StatusBadge status={status} />}
              <Dialog.Close asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <X className="h-4 w-4" />
                </Button>
              </Dialog.Close>
            </div>
          </div>

          {/* Body — smooth height transition when content changes */}
          <ContentTransition open={open} triggerKey={isLoading ? 'loading' : isError ? 'error' : hasData ? 'data' : 'empty'}>
            <div className="max-h-[65vh] overflow-y-auto">
              {isLoading && !hasData ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : isError && !hasData ? (
                <div className="flex flex-col items-center gap-2 py-12 text-center">
                <AlertCircle className="h-8 w-8 text-muted-foreground/40" />
                <p className="text-sm text-muted-foreground">{t('sim.fetchError')}</p>
              </div>
            ) : hasData ? (
              <div className="animate-in fade-in slide-in-from-bottom-1 duration-300">
                {/* Monthly usage bar */}
                <div className="px-5 py-4 border-b border-border/60">
                  <div className="flex items-baseline justify-between mb-2">
                    <p className="text-xs font-semibold">{t('sim.monthlyUsage')}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatMB(monthUsed)} / {formatMB(totalPackage)}
                    </p>
                  </div>
                  <div className="h-3 rounded-full bg-muted overflow-hidden">
                    <div
                      className={cn(
                        'h-full rounded-full transition-all duration-700 ease-out',
                        usagePercent > 90 ? 'bg-destructive' : usagePercent > 70 ? 'bg-amber-500' : 'bg-emerald-500'
                      )}
                      style={{ width: `${usagePercent}%` }}
                    />
                  </div>
                  <div className="flex justify-between mt-1.5">
                    <p className="text-[10px] text-muted-foreground">{usagePercent.toFixed(1)}% {t('sim.used')}</p>
                    <p className="text-[10px] text-muted-foreground">{formatMB(monthRemaining)} {t('sim.remaining')}</p>
                  </div>
                </div>

                {/* Stats grid */}
                <div className="grid grid-cols-3 gap-px bg-border/40">
                  <div className="bg-background px-4 py-3 text-center">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{t('sim.totalUsed')}</p>
                    <p className="text-sm font-semibold mt-0.5">{formatMB(totalUsed)}</p>
                  </div>
                  <div className="bg-background px-4 py-3 text-center">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{t('sim.dataAllowance')}</p>
                    <p className="text-sm font-semibold mt-0.5">{dataFlow > 0 ? formatMB(dataFlow) : formatMB(totalPackage)}</p>
                  </div>
                  <div className="bg-background px-4 py-3 text-center">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{t('sim.remainingData')}</p>
                    <p className="text-sm font-semibold mt-0.5">{formatMB(remainFlow)}</p>
                  </div>
                </div>

                {/* Detail rows */}
                <div className="px-5 py-2">
                  <InfoRow label={t('sim.iccid')} value={iccid} mono />
                  {imei && <InfoRow label={t('sim.imei')} value={imei} mono />}
                  {imsi && <InfoRow label={t('sim.imsi')} value={imsi} mono />}
                  <InfoRow label={t('sim.package')} value={packageDisplay} />
                  {productCode && <InfoRow label={t('sim.product')} value={productCode.split('-').slice(0, 4).join(' ')} />}
                  <InfoRow label={t('sim.packageExpiry')} value={packageEndDate ? packageEndDate.split(' ')[0] : null} />
                  {activateDate && <InfoRow label={t('sim.activatedOn')} value={activateDate.split(' ')[0]} />}
                  {expectedEndDate && <InfoRow label={t('sim.expectedEnd')} value={expectedEndDate.split(' ')[0]} />}
                  {network && <InfoRow label={t('sim.network')} value={network} />}
                  {isRealName != null && <InfoRow label={t('sim.realName')} value={isRealName ? 'Yes' : 'No'} />}
                  {isIMEIPool != null && <InfoRow label={t('sim.imeiPool')} value={isIMEIPool ? 'Yes' : 'No'} />}
                  {imeiPoolStatus != null && <InfoRow label={t('sim.imeiPoolStatus')} value={String(imeiPoolStatus)} />}
                </div>
              </div>
            ) : null}
            </div>
          </ContentTransition>

          {/* Footer */}
          <div className="flex items-center justify-between px-5 py-3 border-t border-border/60 bg-muted/20">
            <p className="text-[10px] text-muted-foreground">
              {isFetching ? t('sim.refreshing') : t('sim.cachedHint')}
            </p>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 text-xs gap-1.5"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              <RefreshCw className={cn('h-3.5 w-3.5', isFetching && 'animate-spin')} />
              {t('sim.refresh')}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
